/**
 * Férias e afastamentos do funcionário. A conta das férias é a da CLT em linha reta: a cada 12 meses de contrato nasce um
 * período aquisitivo com 30 dias de direito, e o empregador tem os 12 meses seguintes (período concessivo) para conceder —
 * passou disso, paga em dobro (art. 137). O sistema NÃO reduz o direito por faltas (art. 130) nem zera o período por
 * afastamento longo (art. 133): isso depende da folha. Serve para ninguém ser pego de surpresa pelo prazo.
 */
import { z } from 'zod';
import { DataISO, Id } from './comum.js';

export const TipoOcorrencia = z.enum(['FERIAS', 'AFASTAMENTO']);
export type TipoOcorrencia = z.infer<typeof TipoOcorrencia>;

export const OcorrenciaBody = z.object({
  tipo: TipoOcorrencia,
  inicio: DataISO,
  /** afastamento: previsão de retorno (último dia afastado); vazio = sem previsão. Férias: o sistema calcula pelo número de dias */
  fim: DataISO.nullable().default(null),
  /** férias: dias de descanso (5 a 30) */
  dias: z.number().int().min(1).max(30).nullable().default(null),
  /** férias: dias vendidos (abono pecuniário, até 1/3) */
  abonoDias: z.number().int().min(0).max(10).default(0),
  /** férias: início do período aquisitivo a que se referem */
  aquisitivoInicio: DataISO.nullable().default(null),
  /** afastamento: motivo pela tabela 18 do eSocial */
  motivo: z.string().regex(/^\d{2}$/).nullable().default(null),
  observacao: z.string().trim().max(500).nullable().default(null),
});
export type OcorrenciaBody = z.infer<typeof OcorrenciaBody>;

export const Ocorrencia = z.object({
  id: Id, funcionarioId: Id, tipo: TipoOcorrencia, inicio: DataISO, fim: DataISO.nullable(), dias: z.number().int().nullable(), abonoDias: z.number().int(),
  aquisitivoInicio: DataISO.nullable(), aquisitivoFim: DataISO.nullable(), motivo: z.string().nullable(), motivoRotulo: z.string().nullable(), observacao: z.string().nullable(),
  /** hoje está dentro do período */
  emCurso: z.boolean(), criadoEm: z.string(),
});
export type Ocorrencia = z.infer<typeof Ocorrencia>;

export const SituacaoFerias = z.enum(['EM_AQUISICAO', 'A_CONCEDER', 'VENCENDO', 'VENCIDAS', 'QUITADAS']);
export type SituacaoFerias = z.infer<typeof SituacaoFerias>;
export const ROTULO_SITUACAO_FERIAS: Record<SituacaoFerias, string> = { EM_AQUISICAO: 'adquirindo', A_CONCEDER: 'a conceder', VENCENDO: 'prazo acabando', VENCIDAS: 'vencidas (em dobro)', QUITADAS: 'quitadas' };

export const PeriodoFerias = z.object({
  aquisitivoInicio: DataISO, aquisitivoFim: DataISO,
  /** último dia para o descanso TERMINAR sem pagar em dobro */
  concessivoFim: DataISO,
  direito: z.number().int(), gozados: z.number().int(), abono: z.number().int(), saldo: z.number().int(),
  /** último dia para COMEÇAR o saldo inteiro e terminar dentro do prazo */
  limiteParaIniciar: DataISO.nullable(),
  /** último dia para entregar o aviso de férias (30 dias antes do início — art. 135) */
  limiteDoAviso: DataISO.nullable(),
  situacao: SituacaoFerias,
});
export type PeriodoFerias = z.infer<typeof PeriodoFerias>;

export const FeriasEAfastamentos = z.object({ temDireitoAFerias: z.boolean(), periodos: z.array(PeriodoFerias), ocorrencias: z.array(Ocorrencia) });
export type FeriasEAfastamentos = z.infer<typeof FeriasEAfastamentos>;

/** Painel do departamento pessoal: o que está para acontecer com os funcionários de todos os clientes. */
export const PainelPessoalItem = z.object({
  tipo: z.enum(['FERIAS_VENCIDAS', 'FERIAS_VENCENDO', 'EXPERIENCIA', 'AFASTADO', 'DE_FERIAS', 'RETORNO', 'ESOCIAL_INCOMPLETO', 'ALTERACAO_AGENDADA', 'ALTERACAO_SEM_ADITIVO']),
  clienteId: Id, clienteNome: z.string(), funcionarioId: Id, funcionarioNome: z.string(), cargo: z.string().nullable(),
  /** a data que importa para o item (limite, término, retorno) */
  data: DataISO.nullable(), dias: z.number().int().nullable(), detalhe: z.string(),
});
export type PainelPessoalItem = z.infer<typeof PainelPessoalItem>;
export const PainelPessoal = z.object({ funcionariosAtivos: z.number().int(), clientesComFuncionarios: z.number().int(), itens: z.array(PainelPessoalItem) });
export type PainelPessoal = z.infer<typeof PainelPessoal>;

// ------------------------------------------------------------------ datas (tudo em UTC, dia cheio)

const paraData = (iso: string) => new Date(`${iso}T00:00:00Z`);
const paraIso = (d: Date) => d.toISOString().slice(0, 10);
export const somarDiasIso = (iso: string, n: number): string => { const d = paraData(iso); d.setUTCDate(d.getUTCDate() + n); return paraIso(d); };
export const diasEntre = (de: string, ate: string): number => Math.round((paraData(ate).getTime() - paraData(de).getTime()) / 86_400_000);
/** mesmo dia N anos depois; 29/02 em ano comum vira 28/02 */
function somarAnos(iso: string, n: number): string {
  const [a, m, d] = iso.split('-').map(Number) as [number, number, number];
  const ultimo = new Date(Date.UTC(a + n, m, 0)).getUTCDate();
  return `${a + n}-${String(m).padStart(2, '0')}-${String(Math.min(d, ultimo)).padStart(2, '0')}`;
}

/** Vínculos que têm férias (ou recesso, no estágio). Sócio, autônomo e temporário não entram nesta conta. */
export const temFerias = (vinculo: string): boolean => !['PRO_LABORE', 'AUTONOMO', 'TEMPORARIO'].includes(vinculo);

export const DIAS_AVISO_FERIAS = 60;

/**
 * Os períodos aquisitivos desde a admissão, com o que já foi gozado/vendido em cada um.
 * `ate` = até quando contar (o desligamento, se houver; senão hoje). Mostra os períodos com saldo e os dois últimos quitados.
 */
export function periodosDeFerias(dataAdmissao: string, ferias: Array<{ aquisitivoInicio: string | null; dias: number | null; abonoDias: number }>, hoje: string, ate: string = hoje): PeriodoFerias[] {
  const periodos: PeriodoFerias[] = [];
  for (let k = 0; k < 60; k++) {
    const inicio = somarAnos(dataAdmissao, k);
    if (inicio > ate) break;
    const fim = somarDiasIso(somarAnos(dataAdmissao, k + 1), -1);
    const concessivoFim = somarDiasIso(somarAnos(dataAdmissao, k + 2), -1);
    const doPeriodo = ferias.filter((f) => f.aquisitivoInicio === inicio);
    const gozados = doPeriodo.reduce((s, f) => s + (f.dias ?? 0), 0); const abono = doPeriodo.reduce((s, f) => s + f.abonoDias, 0);
    const direito = 30; const saldo = Math.max(0, direito - gozados - abono);
    const completo = fim <= ate;
    const limiteParaIniciar = completo && saldo > 0 ? somarDiasIso(concessivoFim, -(saldo - 1)) : null;
    const situacao: SituacaoFerias = !completo ? 'EM_AQUISICAO' : saldo === 0 ? 'QUITADAS' : limiteParaIniciar! < hoje ? 'VENCIDAS' : diasEntre(hoje, limiteParaIniciar!) <= DIAS_AVISO_FERIAS + 30 ? 'VENCENDO' : 'A_CONCEDER';
    periodos.push({ aquisitivoInicio: inicio, aquisitivoFim: fim, concessivoFim, direito, gozados, abono, saldo, limiteParaIniciar, limiteDoAviso: limiteParaIniciar ? somarDiasIso(limiteParaIniciar, -30) : null, situacao });
  }
  const abertos = periodos.filter((p) => p.situacao !== 'QUITADAS');
  const quitados = periodos.filter((p) => p.situacao === 'QUITADAS').slice(-2);
  return [...quitados, ...abertos].sort((a, b) => (a.aquisitivoInicio < b.aquisitivoInicio ? -1 : 1));
}
